import hoist from 'hoist-non-react-statics';
import React, {Component} from 'react';
import {any, map, reduce} from '@republic/foundation/lang/array';
import {isArray, isEq, isEqual, isFunction, isObject} from '@republic/foundation/lang/is';
import {keys} from '@republic/foundation/lang/object';
import {Stream, Source, ready} from '@republic/foundation/streams';
import {createComponent} from './components';

const
    createEnhancer = (fn, exclude) => (
        WrappedComponent => (
            hoist(
                fn(WrappedComponent),
                WrappedComponent,
                exclude))),

    wrapped = (WithComponent, selector, options) => {
        const
            resolvedSelector = (
                isFunction(selector) ?
                    selector :
                    () => (selector || {}));

        return (
            createEnhancer(
                WrappedComponent => (
                    createComponent(
                        `Wrapped${
                            WithComponent.displayName ||
                            WithComponent.name ||
                            'Component'
                        }(${
                            WrappedComponent.displayName ||
                            WrappedComponent.name ||
                            'Component'
                        })`,
                        options,
                        ({children, ...props}) => (
                            <WithComponent {...resolvedSelector(props)}>
                                <WrappedComponent {...{children, ...props}} />
                            </WithComponent>)))));
    },

    constants = (selector, options) => {
        const
            resolvedSelector = (
                isFunction(selector) ?
                    selector :
                    () => selector);

        return (
            createEnhancer(
                WrappedComponent => (
                    createComponent(
                        `Constants(${
                            WrappedComponent.displayName ||
                            WrappedComponent.name ||
                            'Component'
                        })`,
                        options,
                        class Constants extends Component {
                            constructor(props) {
                                super(props);
                                this.constants = resolvedSelector(props);
                            }

                            componentWillUnmount() {
                                this.constants = null;
                            }

                            render() {
                                const {children} = this.props;

                                return (
                                    <WrappedComponent {...{
                                        ...this.props,
                                        ...this.constants,
                                        children
                                    }} />);
                            }
                        }))));
    },

    subscribe = (
        selector,
        combiner,
        props,
        {
            getDerivedStateFromProps = null,
            compareSelection = isEqual,
            compareProps = isEqual,
            compareCombination = isEqual,
            compareSubscription = isEqual,
            ...options
        } = {}) => {

        const
            resolvedGetDerivedStateFromProps = (
                isFunction(getDerivedStateFromProps) ?
                    (props, state) => {
                        const derived = getDerivedStateFromProps(props, state.derived);

                        return (
                            derived ?
                                {
                                    ...state,
                                    derived
                                } :
                                state);
                    } :
                    () => null),
            resolvedSelector = (
                isFunction(selector) ?
                    selector :
                    () => selector),
            resolvedCombiner = (
                isFunction(combiner) ?
                    combiner :
                    (isArray(combiner) ?
                        selection => (
                            Stream.combineAll(
                                map(combiner,
                                    name => selection[name]))) :
                        (selection => selection))),
            resolvedProps = (
                isFunction(props) ?
                    props :
                    (isObject(props) ?
                        state => (
                            reduce(
                                keys(props),
                                (data, name) => {
                                    data[name] = state[props[name]];
                                    return data;
                                },
                                {})) :
                        (state, props) => ({
                            ...props,
                            ...state
                        })));

        return (
            createEnhancer(
                WrappedComponent => (
                    createComponent(
                        `Subscribe(${
                            WrappedComponent.displayName ||
                            WrappedComponent.name ||
                            'Component'
                        })`,
                        options,
                        class Subscribe extends Component {
                            static getDerivedStateFromProps(props, state) {
                                return resolvedGetDerivedStateFromProps(props, state);
                            }

                            constructor(props) {
                                super(props);
                                this.state = {values: {}, derived: {}};
                                this.ready = false;
                                this.dirty = false;
                                this.promise = null;
                                this.values = null;
                                this.source = new Source();
                                this.stream = (
                                    this.source
                                    .scan(
                                        (prev, next) => next,
                                        () => this.props));
                                this.unsubscribe = null;
                            }

                            componentDidMount() {
                                this.unsubscribe = (
                                    Stream.combineAll([
                                        (this.stream
                                        .map(resolvedSelector)
                                        .unique(compareSelection)
                                        .map(resolvedCombiner)
                                        .select()
                                        .unique(compareCombination)),

                                        this.stream
                                        .unique(compareProps)
                                    ])
                                    .map(
                                        ([state, props]) => (
                                            resolvedProps(state, props)))
                                    .unique(compareSubscription)
                                    .subscribe(values => {
                                        this.values = values;
                                        if (!this.promise) {
                                            this.promise = (
                                                ready()
                                                .then(() => {
                                                    if (this.promise) {
                                                        const values = this.values;

                                                        if (!this.ready) {
                                                            this.ready = true;
                                                        }
                                                        if (!this.dirty) {
                                                            this.dirty = true;
                                                        }
                                                        this.promise = null;
                                                        this.values = null;
                                                        this.setState({values});
                                                    }
                                                }));
                                        }
                                    }));
                            }

                            componentWillUnmount() {
                                const unsubscribe = this.unsubscribe;

                                if (unsubscribe) {
                                    this.unsubscribe = null;
                                    unsubscribe();
                                }
                                this.promise = null;
                            }

                            shouldComponentUpdate(props, state) {
                                const
                                    {children: nextChildren, ...next} = props,
                                    {children: prevChildren, ...prev} = this.props,
                                    {derived: nextDerived} = state,
                                    {derived: prevDerived} = this.state;

                                if (!isEqual(next, prev)) {
                                    this.source.next(props);
                                }
                                if (this.dirty) {
                                    this.dirty = false;
                                    return true;
                                } else {
                                    return (
                                        nextChildren !== prevChildren ||
                                        nextDerived !== prevDerived);
                                }
                            }

                            render() {
                                const {children} = this.props;

                                return (
                                    this.ready ?
                                        <WrappedComponent {...{
                                            ...this.state.values,
                                            ...this.state.derived,
                                            children
                                        }} /> :
                                        null);
                            }
                        }))));
    },

    stream = (
        names,
        {
            compareProps = isEq,
            ...options
        } = {}) => {

        const
            resolvedCompareProps = (
                reduce(
                    names,
                    (compare, name) => {
                        compare.push(
                            isFunction(compareProps) ?
                                compareProps :
                                ((compareProps && isFunction(compareProps[name])) ?
                                    compareProps[name] :
                                    isEq));
                        return compare;
                    },
                    []));

        return (
            createEnhancer(
                WrappedComponent => (
                    createComponent(
                        `Stream(${
                            WrappedComponent.displayName ||
                            WrappedComponent.name ||
                            'Component'
                        })`,
                        options,
                        class Stream extends Component {
                            constructor(props) {
                                super(props);
                                this.source = new Source();
                                this.stream = (
                                    this.source
                                    .scan(
                                        (prev, next) => next,
                                        () => this.props)
                                    .latest());
                                this.streams = (
                                    reduce(
                                        names,
                                        (streams, name) => {
                                            streams[name] = (
                                                this.stream
                                                .map(({[name]: value}) => value)
                                                .unique()
                                                .latest());
                                            return streams;
                                        },
                                        {}));
                            }

                            shouldComponentUpdate(next) {
                                const prev = this.props;

                                if (any(names,
                                        (name, i) => (
                                            !resolvedCompareProps[i](
                                                next[name],
                                                prev[name])))) {

                                    this.source.next(
                                        reduce(
                                            names,
                                            (values, name) => {
                                                values[name] = next[name];
                                                return values;
                                            },
                                            {}));
                                }
                                return (
                                    any(keys(next),
                                        name => (
                                            names.indexOf(name) < 0 &&
                                            next[name] !== prev[name])));
                            }

                            render() {
                                return (
                                    <WrappedComponent {...{
                                        ...this.props,
                                        ...this.streams
                                    }} />);
                            }
                        }))));
    };

export {
    createEnhancer,
    wrapped,
    constants,
    subscribe,
    stream
};
